import { z } from 'zod';
import { DocId, IsoTimestamp } from '../common.js';
import { classificationFields, sourceDefaultClassificationFields } from '../classification.js';
import { documentVersionFields } from './document-version.js';
/**
 * Slice 5 — admin upload pipeline.
 *
 * Two top-level Firestore collections drive Andre's Beheer Kennisbank UI:
 *
 *   `ingest_jobs/{id}` — short-lived job records. Created on upload-url
 *      handshake, finalized on file confirm, transitioned to running once
 *      the Cloud Run job picks up the work, terminal once chunking + embed
 *      completes (or fails). Polled by the UI for progress.
 *
 *   `kennisbank_ring1_documents/{filename}` — per-source-document registry
 *      with chunk count + last ingestedAt + last actor. Decouples "what
 *      Andre sees in the list" from a full chunk table scan.
 *
 * Both are documented exceptions to CLAUDE.md hard rule 1 (multi-tenant)
 * because Ring 1 is a single shared corpus across every tenant. Suppohrt
 * admin RBAC is enforced at every route.
 */
export const IngestJobAction = z.enum(['upload', 'reingest', 'delete']);
/**
 * Job dispatch discriminator. `document` is the legacy per-file PDF/DOCX/
 * Confluence path (the `action` + `filename` fields drive the worker).
 * `code-source-sync` is the bulk code-ingest path: one job per CodeSource
 * sync, carrying a GCS manifest of every staged blob — the worker reads
 * the manifest and ingests each file into kennisbank_ring{0,1} as a code
 * chunk row.
 *
 * `confluence-source-sync` is the manual Confluence subtree sync moved out
 * of the Express request handler (it routinely runs longer than Cloud Run's
 * 30 min service timeout). One job per /sources/:id/sync click; the Node
 * entrypoint reads `confluenceSourceId` + `confluenceSyncForce` off the
 * job doc and orchestrates per-page document jobs the same way the inline
 * code did.
 */
export const IngestJobKind = z.enum([
    'document',
    'code-source-sync',
    'confluence-source-sync',
    'jira-source-sync',
]);
/**
 * State machine, mirrored by `system_status/ingest_lock` for at-most-one
 * concurrency. Order: pending → staging → validated → queued → invoking →
 * running → succeeded | failed | cancelled | expired. Terminal states:
 * succeeded, failed, cancelled, expired.
 *
 * `queued` (bulk upload, 2026-09): a validated upload waits for the single
 * documents-lock holder instead of the browser retrying 409s. Whichever
 * worker holds the lock claims the oldest `queued` job (by `queuedAt`) when
 * it finishes its current one, so a folder of files drains server-side after
 * the tab closes.
 */
export const IngestJobStatus = z.enum([
    'pending', // upload-url minted, awaiting client PUT
    'staging', // client uploaded, awaiting finalize
    'validated', // server-side validation passed (size, MIME, md5, pdf integrity)
    'queued', // validated, waiting for the documents lock (server-side drain)
    'invoking', // Cloud Run job invocation in flight (lock=acquiring)
    'running', // Cloud Run job started (lock=running, executionName populated)
    'succeeded',
    'failed',
    'cancelled',
    'expired', // never uploaded (abandoned `pending` > 24h) — set by the janitor
]);
export const TERMINAL_INGEST_JOB_STATUSES = new Set([
    'succeeded',
    'failed',
    'cancelled',
    'expired',
]);
/**
 * Groups the jobs one folder drop created so progress can be read back from
 * the server (survives reload, visible to every admin). Format is checked on
 * every route that accepts it.
 */
export const IngestBatchId = z.string().regex(/^batch_[a-z0-9_]{6,64}$/);
/**
 * Aggregation metadata stamped onto `kennisbank_ring{N}_documents/jira-*.md`
 * registry docs (2026-06-09 jira-aggregation design, slice 0). These are the
 * queryable columns `jira_aggregate` group-bys run over. Carried on the
 * IngestJob as one map (`jiraFields`) so the Python worker can spread it
 * without per-field plumbing; nulls overwrite on resync (a ticket can lose
 * its assignee or become unresolved again). Keep field names in lockstep
 * with the worker's whitelist in `scripts/ingest-documents.py`.
 */
export const JiraAggregationFields = z.object({
    jiraIssueType: z.string().max(120).nullable(),
    jiraStatus: z.string().max(120).nullable(),
    jiraPriority: z.string().max(120).nullable(),
    jiraLabels: z.array(z.string().max(255)).max(50),
    /** JSM "Customer Request Type" display name — null outside service desks. */
    jiraRequestType: z.string().max(255).nullable(),
    jiraResolution: z.string().max(120).nullable(),
    jiraAssigneeAccountId: z.string().max(128).nullable(),
    jiraAssigneeDisplayName: z.string().max(255).nullable(),
    jiraReporterAccountId: z.string().max(128).nullable(),
    jiraReporterDisplayName: z.string().max(255).nullable(),
    /** Jira `fields.created`, normalised to UTC ISO so string range queries
     *  are correct (Jira returns offset timestamps like `+0200`). */
    jiraCreatedAt: z.string().max(40).nullable(),
    /** Jira `fields.resolutiondate`, normalised to UTC ISO. */
    jiraResolvedAt: z.string().max(40).nullable(),
    jiraStoryPoints: z.number().nullable(),
});
export const IngestJob = z.object({
    id: DocId,
    /**
     * Dispatch discriminator. Defaults to `'document'` for back-compat with
     * the existing per-file ingest path (`action` + `filename` required).
     * When `'code-source-sync'`, the worker dispatches into the bulk
     * code-ingest pipeline and reads the code-* fields below instead.
     */
    kind: IngestJobKind.default('document'),
    /** Required when kind=document. Omitted for code-source-sync jobs. */
    action: IngestJobAction.optional(),
    /** Required when kind=document. Lower-cased, no path. */
    filename: z.string().min(1).max(255).optional(),
    /** sha256 of canonical filename — audit-log linkage; document-only. */
    filenameHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    status: IngestJobStatus,
    /** Server-minted GCS path under the staging bucket. Only set for `upload`. */
    gcsStagingPath: z.string().optional(),
    /** Bytes — populated on staging from the upload-url request. */
    sizeBytes: z.number().int().nonnegative().optional(),
    /** Sniffed Content-Type — bound to the signed URL on `upload`. */
    contentType: z.string().max(120).optional(),
    /** md5 of the staged object, captured on finalize and verified server-side. */
    md5Base64: z.string().optional(),
    /** Long-running operation name from `jobs.run`. */
    operationName: z.string().optional(),
    /** Cloud Run execution resource name — populated by the job poller. */
    executionName: z.string().optional(),
    /** Last error code shown to the user (Dutch user-facing). */
    errorCode: z.string().optional(),
    /** Internal error trace — never returned to the client. */
    errorDetail: z.string().optional(),
    /** kb_admin user id who kicked off the job. */
    actorUserId: DocId,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    /** When the staging signed URL expires. */
    stagingExpiresAt: IsoTimestamp.optional(),
    /** Set when the job enters `queued`; the drain claims oldest-first on it. */
    queuedAt: IsoTimestamp.optional(),
    /** Bulk-upload batch this job belongs to (see `ingest_batches/{id}`). */
    batchId: IngestBatchId.optional(),
    /**
     * Tier the document belongs to. `ring0` = OneNebula internal docs
     * (handbook, policies, ways-of-working) — RBAC-gated. `ring1` =
     * product / customer-facing docs — broader access. Defaults to
     * `ring1` for back-compat with jobs created before this field
     * existed; the Beheer admin UI sets it explicitly per upload zone.
     */
    ring: z.enum(['ring0', 'ring1']).default('ring1'),
    /**
     * Set when the doc was ingested via /admin/confluence — links the doc
     * back to its source. Propagated through the job onto the registry row
     * so the Confluence admin UI can list pages per source without joining.
     */
    confluenceSourceId: DocId.optional(),
    /**
     * Confluence page version.number at last successful ingest. Used by
     * /admin/confluence/.../sync to skip unchanged pages.
     */
    confluenceVersion: z.number().int().nonnegative().optional(),
    /**
     * Carries the `force=true` query flag from the manual sync route through
     * to the `confluence-source-sync` Cloud Run Job entrypoint. Reading it off
     * the job doc keeps the entrypoint signature small (only `JOB_ID` env var)
     * and means the same job record is the audit-grade source of truth for
     * what was asked.
     */
    confluenceSyncForce: z.boolean().optional(),
    /**
     * Display title for the document — for Confluence ingests this is the
     * actual page title (not the synthetic filename slug). Propagated to
     * `DocumentRegistryEntry.documentTitle` by the Python worker.
     */
    documentTitle: z.string().min(1).max(500).optional(),
    /**
     * Confluence page author display name (resolved via v2/users) at the
     * time of ingest. Propagated to `DocumentRegistryEntry.author` by the
     * Python worker. Falls back to the raw accountId if the user lookup fails.
     */
    confluenceAuthor: z.string().min(1).max(255).optional(),
    // ---------- Jira ingest fields (set when ingested via /admin/jira-sources) -
    // Per-issue document jobs carry these so the Python worker stamps the
    // registry row: jiraSourceId links the doc back to its project source
    // (so the admin UI can list issues per source), jiraUpdatedAt is the
    // delta cursor (Jira's `updated` timestamp at last ingest — compared on the
    // next sync to skip unchanged issues, mirroring confluenceVersion).
    /** Links the doc back to its `jira_sources/{id}` project source. */
    jiraSourceId: DocId.optional(),
    /** The Jira issue key, e.g. "OK-82". */
    jiraIssueKey: z.string().min(1).max(60).optional(),
    /** Jira `fields.updated` (ISO) at ingest. Delta cursor for the next sync. */
    jiraUpdatedAt: z.string().min(1).max(40).optional(),
    /** Carries the force=true flag through to the jira-sync Cloud Run Job. */
    jiraSyncForce: z.boolean().optional(),
    /** Image backfill: restrict a (force) sync to these issue keys — only they
     *  get the expensive detail+image+embed; the rest of the project is skipped
     *  cheaply. Carried through to the jira-sync Cloud Run Job. */
    jiraSyncOnlyKeys: z.array(z.string().min(1).max(60)).max(5000).optional(),
    /** Aggregation columns to stamp on the registry doc — see JiraAggregationFields. */
    jiraFields: JiraAggregationFields.optional(),
    // ---------- code-source-sync fields (kind = 'code-source-sync') ----------
    //
    // One job per CodeSource sync. The manifest at `codeManifestPath`
    // enumerates every staged blob; the worker reads it and writes a code
    // chunk row per chunk into the ring's chunks collection.
    /** Links back to `tenants/{tenantId}/code_sources/{id}`. */
    codeSourceId: DocId.optional(),
    /** Pinned commit SHA the sync resolved before listing the tree. */
    codeCommitSha: z.string().min(1).max(64).optional(),
    /** GitHub `owner/repo` (or other gateway-defined ref). For citations. */
    codeRepoRef: z.string().min(1).max(200).optional(),
    /** Branch the sync ran against. */
    codeBranch: z.string().min(1).max(100).optional(),
    /** Owner of the source — Entra oid. Audit linkage only. */
    codeOwnerOid: z.string().min(1).max(200).optional(),
    /**
     * `gs://...` URI of the manifest JSON the backend staged. Schema:
     * `{ files: [{ path, sha, sizeBytes, language, gcsPath }] }`.
     */
    codeManifestPath: z.string().min(1).max(500).optional(),
    /** Number of blobs in the manifest. */
    codeFileCount: z.number().int().nonnegative().optional(),
    /** Total chunk rows the worker wrote on success. */
    codeChunksWritten: z.number().int().nonnegative().optional(),
    /**
     * Carries the `force=true` flag from the manual sync route through to
     * the `code-source-sync` Cloud Run Job entrypoint. Mirrors
     * `confluenceSyncForce` — see commentary above.
     */
    codeSyncForce: z.boolean().optional(),
    // ---------- ISO 27001 information classification (chunk-cascade defaults) ----
    // Cascaded onto every chunk + registry row the worker writes. Operator
    // can override per-doc post-ingest via the admin PATCH route. Defaults
    // resolve to internal (open within the org) + no access groups.
    ...sourceDefaultClassificationFields,
});
export const DocumentRegistryEntry = z.object({
    /** Filename is the doc id — collision-safe across the flat corpus. */
    filename: z.string().min(1).max(255),
    documentTitle: z.string().max(500),
    author: z.string().max(255),
    /** Slice 6: optional secondary author (per-doc only, no cascade). */
    coAuthor: z.string().max(255).optional(),
    /**
     * Slice 6: per-doc metadata overrides. When `metadataOverridden` is true,
     * a future re-ingest preserves these values rather than recomputing from
     * the JSON author registry. Lookup priority at retrieval time: chunk row
     * (which already carries denormalized values populated from these
     * overrides) > registry. Editing any of these fields via PATCH flips
     * `metadataOverridden` to true.
     */
    institution: z.string().max(120).optional(),
    workingGroup: z.string().max(120).optional(),
    documentType: z.string().max(40).optional(),
    metadataOverridden: z.boolean().optional(),
    /** Number of chunks indexed in `kennisbank_ring1` for this filename. */
    chunkCount: z.number().int().nonnegative(),
    /**
     * When true, the source PDF/DOCX is in the canonical bucket and listed
     * in /documenten for download, but no chunks were indexed (typically
     * because OCR/extraction failed and the operator chose "Alleen als
     * download bewaren"). Retrieval is naturally blind to these (no chunks
     * to match), so the agent never cites them — the practitioner can still
     * find and download the original from /documenten with a clear
     * "Alleen download" badge so they know the assistant won't quote it.
     */
    documentOnly: z.boolean().optional(),
    /** Bytes of the source on GCS. */
    sizeBytes: z.number().int().nonnegative().optional(),
    contentType: z.string().max(120).optional(),
    /** Last successful ingest. Missing entries = never ingested. */
    lastIngestedAt: IsoTimestamp.optional(),
    /**
     * The document's OWN date (authored / published) as RFC3339 — distinct
     * from `lastIngestedAt` (when it was indexed). Resolved at ingest from
     * file metadata or a date in the filename; `documentDateSource` records
     * which. Surfaced in /documenten + on citation cards. Absent when the
     * ingest couldn't resolve a plausible date.
     */
    documentDate: IsoTimestamp.optional(),
    documentDateSource: z
        .enum(['pdf_metadata', 'docx_metadata', 'pptx_metadata', 'filename'])
        .optional(),
    lastActorUserId: DocId.optional(),
    /** Last job that touched this filename — for status-card linkage. */
    lastJobId: DocId.optional(),
    /** Tombstone: set by DELETE to suppress the filename from list responses. */
    deletedAt: IsoTimestamp.optional(),
    /**
     * When true, the page is tombstoned AND the next sync MUST NOT re-ingest
     * it. Use this when an admin permanently doesn't want a page in the
     * knowledgebase (without removing it from the Confluence subtree, which
     * would be the cleaner source-of-truth fix). Toggle off via an unblock
     * action to allow a future sync to re-ingest. Only meaningful while
     * deletedAt is also set.
     */
    blockedFromIngest: z.boolean().optional(),
    /**
     * Top-N high-level topics for the practitioner /documenten browse surface.
     * Auto-populated by the Python ingest's specialisation_tagger using the
     * shared CANONICAL_SPECIALISATIONS list. Cap of 3 is a UI-density product call (table-cell chip rail
     * stays scannable). Andre inline-edits via Beheer Kennisbank when the
     * auto-tag gets it wrong (slice-6 EditableCell pattern).
     */
    specialisations: z.array(z.string().min(1).max(60)).max(10).optional(),
    /**
     * Trust tier for the chip rail on /documenten. Server-tagged at list
     * time based on which Firestore collection the doc came from
     * (kennisbank_ring1_documents → 'ring1', kennisbank_ring0_documents →
     * 'ring0'). Defaults to 'ring1' — older clients + storage paths that
     * predate Ring 0 simply behave as before.
     */
    ring: z.enum(['ring0', 'ring1']).optional(),
    /**
     * Set when the doc was ingested via /admin/confluence — links the doc
     * back to its source. Denormalised onto the registry row so the
     * /admin/confluence per-source pages list can filter by it without a
     * cross-collection join.
     */
    confluenceSourceId: DocId.optional(),
    /**
     * Confluence page version.number at last successful ingest. Used by
     * /admin/confluence/.../sync to skip unchanged pages.
     */
    confluenceVersion: z.number().int().nonnegative().optional(),
    /**
     * Set when the doc was ingested via /admin/jira-sources — links the issue
     * back to its project source so the Jira admin per-source list can filter
     * by it without a cross-collection join.
     */
    jiraSourceId: DocId.optional(),
    /**
     * Jira `fields.updated` (ISO) at last successful ingest. Used by
     * /admin/jira-sources/.../sync to skip unchanged issues (delta cursor).
     */
    jiraUpdatedAt: z.string().min(1).max(40).optional(),
    // Aggregation columns (2026-06-09 design, slice 0) — spread from the
    // IngestJob's `jiraFields` map by the Python worker, and patched directly
    // by the one-shot backfill script. Nullable-by-value, optional-by-presence:
    // pre-slice-0 rows simply lack them until backfill/resync.
    jiraIssueType: z.string().max(120).nullable().optional(),
    jiraStatus: z.string().max(120).nullable().optional(),
    jiraPriority: z.string().max(120).nullable().optional(),
    jiraLabels: z.array(z.string().max(255)).max(50).optional(),
    jiraRequestType: z.string().max(255).nullable().optional(),
    jiraResolution: z.string().max(120).nullable().optional(),
    jiraAssigneeAccountId: z.string().max(128).nullable().optional(),
    jiraAssigneeDisplayName: z.string().max(255).nullable().optional(),
    jiraReporterAccountId: z.string().max(128).nullable().optional(),
    jiraReporterDisplayName: z.string().max(255).nullable().optional(),
    jiraCreatedAt: z.string().max(40).nullable().optional(),
    jiraResolvedAt: z.string().max(40).nullable().optional(),
    jiraStoryPoints: z.number().nullable().optional(),
    // ---------- ISO 27001 information classification --------------------------
    // `classification` is the data-sensitivity tier; `accessGroups` lists the
    // app-internal group names (mapped from Entra) that can read the chunks.
    // `piiFlags` is informational only — surfaced in the admin UI so the
    // operator knows what's in the doc; doesn't gate retrieval. Older rows
    // without these fields read as classification=internal, no access groups,
    // no PII flags (the Zod default fires on parse).
    ...classificationFields,
    // ---------- Document-version detection (see ./document-version.ts) -------
    ...documentVersionFields,
});
/** Why the browser skipped a file before upload (pre-flight summary). */
export const IngestBatchSkipReason = z.enum([
    'unsupported_type',
    'too_large',
    'empty',
    'identical_existing', // same content already ingested (contentMd5 match)
    'duplicate_in_drop', // same content as another file in this drop
    'collision_skipped', // name exists and the admin chose Skip
]);
/**
 * `ingest_batches/{batchId}` — header row for one bulk upload. Per-file state
 * lives on the `ingest_jobs` rows carrying the same `batchId`; counts are
 * computed with aggregation queries and frozen into `finalCounts` once the
 * batch has nothing left in flight, so finished batches cost no further
 * reads.
 */
export const IngestBatchCounts = z.object({
    uploading: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
export const IngestBatch = z.object({
    id: IngestBatchId,
    label: z.string().max(200),
    ring: z.enum(['ring0', 'ring1']),
    /** Files the browser planned to upload after pre-flight. */
    plannedCount: z.number().int().nonnegative(),
    skipped: z
        .array(z.object({ filename: z.string().max(1024), reason: IngestBatchSkipReason }))
        .max(2000),
    actorUserId: DocId,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    completedAt: IsoTimestamp.optional(),
    finalCounts: IngestBatchCounts.optional(),
});
//# sourceMappingURL=ingest-job.js.map